import React, { useState, useEffect, useCallback } from 'react';
import MapComponent from './components/MapComponent.tsx';
import ActivityFeed from './components/ActivityFeed.tsx';
import AndroidDownloadBanner from './components/AndroidDownloadBanner.tsx';
import SupportModal from './SupportModal.tsx';
import QuickReportModal from './QuickReportModal.tsx';
import StationSuggestionModal from './StationSuggestionModal.tsx';
import StationLocationUpdateModal from './StationLocationUpdateModal.tsx';
import ContactModal from './ContactModal.tsx';
import SkeletonLoader from './SkeletonLoader.tsx';
import Pagination from './Pagination.tsx';
import { getRelativeTime } from './utils/time.ts';
import { analytics } from './services/googleAnalytics.ts';
import amplitudeAnalytics from './services/amplitude.ts';
import { addToFavorites, removeFromFavorites, isFavorite, getFavoriteIds } from './utils/favorites.ts';



interface Station {
  id: number;
  name: string;
  brand: string;
  latitude: number;
  longitude: number;
  address: string;
  quartier?: string;
  commune: string;
  latest_report?: {
    fuel_type: string;
    status: string;
    price?: number;
    comment?: string;
    created_at: string;
    confirm_count?: number;
    contest_count?: number;
  };
}

function App() {
  const [stations, setStations] = useState<Station[]>([]);
  const [filteredStations, setFilteredStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map' | 'activity'>('map');
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showQuickReportModal, setShowQuickReportModal] = useState(false);
  const [showStationSuggestionModal, setShowStationSuggestionModal] = useState(false);
  const [showLocationUpdateModal, setShowLocationUpdateModal] = useState(false);
  const [locationUpdateStation, setLocationUpdateStation] = useState<Station | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [favoritesVersion, setFavoritesVersion] = useState(0); // To force re-render when favorites change
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showFilterHint, setShowFilterHint] = useState(true);

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12); // 12 items par page pour une bonne grille
  const [totalPages, setTotalPages] = useState(0);

  // États pour la gestion des erreurs et retry
  const [retryCount, setRetryCount] = useState({ map: 0, paginated: 0 });
  const [totalItems, setTotalItems] = useState(0);
  const [loadingPage, setLoadingPage] = useState(false);
  const [allStationsForMap, setAllStationsForMap] = useState<Station[]>([]); // Toutes les stations pour la carte

  // États des filtres
  const [filters, setFilters] = useState({
    searchTerm: '',
    fuelType: 'all',
    status: 'all',
    brand: 'all',
    quartier: 'all',
    commune: 'all'
  });


  // Charger toutes les stations pour la carte (sans pagination)
  const loadAllStationsForMap = useCallback(async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      console.log('🔗 API_BASE_URL:', API_BASE_URL);

      const response = await fetch(`${API_BASE_URL}/stations`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        // Timeout plus long pour production
        signal: AbortSignal.timeout(10000), // 10 secondes
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error('API response is not an array');
      }

      setAllStationsForMap(data);
      // Reset retry counter on success
      setRetryCount(prev => ({ ...prev, map: 0 }));
    } catch (err) {
      console.error('❌ Erreur lors du chargement de toutes les stations:', err);

      // Limite les tentatives à 3 maximum
      if (retryCount.map < 3) {
        setRetryCount(prev => ({ ...prev, map: prev.map + 1 }));
        setTimeout(() => {
          console.log(`🔄 Retry loading stations... (${retryCount.map + 1}/3)`);
          // Note: This creates a new retry cycle with updated retryCount
          loadAllStationsForMap();
        }, 2000);
      } else {
        console.error('❌ Échec définitif du chargement des stations après 3 tentatives');
        setError('Impossible de charger les stations. Veuillez rafraîchir la page.');
      }
    }
  }, [retryCount.map]);

  // Charger les stations avec pagination pour la vue liste
  const loadStationsPaginated = useCallback(async (page: number = 1, size: number = itemsPerPage) => {
    try {
      setLoadingPage(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

      // Build query parameters with filters
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString()
      });

      if (filters.searchTerm) params.append('search', filters.searchTerm);
      if (filters.brand !== 'all') params.append('brand', filters.brand);
      if (filters.commune !== 'all') params.append('commune', filters.commune);
      if (filters.quartier !== 'all') params.append('quartier', filters.quartier);
      if (filters.fuelType !== 'all') params.append('fuel_type', filters.fuelType);
      if (filters.status !== 'all') params.append('status', filters.status);

      const response = await fetch(`${API_BASE_URL}/stations/paginated?${params}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(10000), // 10 secondes
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🐛 DEBUG loadStationsPaginated - API returned:', data);

      if (!data.stations || !Array.isArray(data.stations)) {
        throw new Error('Invalid paginated response format');
      }

      setStations(data.stations);
      setTotalPages(data.pagination.pages);
      setTotalItems(data.pagination.total);
      setCurrentPage(data.pagination.page);

      setError(null);
      // Reset retry counter on success
      setRetryCount(prev => ({ ...prev, paginated: 0 }));
    } catch (err) {
      console.error('❌ Erreur lors du chargement paginé:', err);

      // Limite les tentatives à 2 maximum pour la pagination
      if (retryCount.paginated < 2) {
        setRetryCount(prev => ({ ...prev, paginated: prev.paginated + 1 }));
        setError('Connexion au serveur impossible. Vérification en cours...');
        setTimeout(() => {
          console.log(`🔄 Retry paginated loading... (${retryCount.paginated + 1}/2)`);
          // Note: This creates a new retry cycle with updated retryCount
          loadStationsPaginated(page, size);
        }, 3000);
      } else {
        console.error('❌ Échec définitif du chargement paginé après 2 tentatives');
        setError('Connexion au serveur impossible. Vérifiez que l\'API backend est démarrée et accessible.');
      }
    } finally {
      setLoadingPage(false);
    }
  }, [itemsPerPage, filters.searchTerm, filters.brand, filters.commune, filters.quartier, filters.fuelType, filters.status, retryCount.paginated]);

  // Fonction de chargement initial optimisée
  const loadStations = useCallback(async () => {
    try {
      // Afficher l'interface immédiatement après 100ms
      setTimeout(() => {
        if (!initialLoadComplete) {
          setInitialLoadComplete(true);
          setLoading(false);
        }
      }, 100);

      // Toujours charger toutes les stations pour les filtres
      await loadAllStationsForMap();

      // Charger différemment selon le mode de vue pour l'affichage
      if (viewMode === 'list') {
        // Pour la liste, utiliser la pagination
        await loadStationsPaginated(currentPage, itemsPerPage);
      }

      setInitialLoadComplete(true);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des stations');
    } finally {
      setLoading(false);
    }
  }, [viewMode, currentPage, itemsPerPage, initialLoadComplete, loadAllStationsForMap, loadStationsPaginated]);



  // Fonction de filtrage
  const applyFilters = useCallback(() => {
    // Utiliser TOUJOURS allStationsForMap comme source pour avoir toutes les stations
    const sourceStations = allStationsForMap;

    // Use the same data source for both views
    if (sourceStations.length === 0) {
      setFilteredStations([]);
      return;
    }

    let filtered = [...sourceStations];

    // Filtre par terme de recherche (nom, adresse, quartier, commune)
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(station =>
        station.name.toLowerCase().includes(term) ||
        station.address.toLowerCase().includes(term) ||
        station.commune.toLowerCase().includes(term) ||
        (station.quartier && station.quartier.toLowerCase().includes(term))
      );
    }

    // Filtre par type de carburant
    if (filters.fuelType !== 'all') {
      filtered = filtered.filter(station => {
        const report = station.latest_report;
        if (!report) return false;

        if (filters.fuelType === 'essence') {
          return report.fuel_type === 'essence' || report.fuel_type === 'both';
        }
        if (filters.fuelType === 'diesel') {
          return report.fuel_type === 'diesel' || report.fuel_type === 'both';
        }
        return true;
      });
    }

    // Filtre par statut
    if (filters.status !== 'all') {
      filtered = filtered.filter(station => {
        const report = station.latest_report;
        if (filters.status === 'out') {
          // Traiter 'low' comme 'out' (rupture) + stations sans info
          return report?.status === 'out' || report?.status === 'low' || !report?.status;
        }
        return report?.status === filters.status;
      });
    }

    // Filtre par marque
    if (filters.brand !== 'all') {
      filtered = filtered.filter(station =>
        station.brand.toLowerCase() === filters.brand.toLowerCase()
      );
    }

    // Filtre par quartier
    if (filters.quartier !== 'all') {
      filtered = filtered.filter(station =>
        station.quartier === filters.quartier
      );
    }

    // Filtre par commune
    if (filters.commune !== 'all') {
      filtered = filtered.filter(station =>
        station.commune === filters.commune
      );
    }

    // Filtre par favoris
    if (showOnlyFavorites) {
      const favoriteIds = getFavoriteIds();
      filtered = filtered.filter(station =>
        favoriteIds.includes(station.id)
      );
    }

    // Trier les stations par statut : disponible > rupture (inclut pas d'info)
    const getStatusPriority = (status?: string) => {
      switch (status) {
        case 'available': return 1; // Priorité la plus haute
        case 'low': return 2; // Traiter "stock faible" comme "rupture"
        case 'out': return 2; // Même priorité que "low" (rupture)
        default: return 2; // Pas d'info traité comme "rupture"
      }
    };

    const sortedFiltered = filtered.sort((a, b) => {
      const statusA = getStatusPriority(a.latest_report?.status);
      const statusB = getStatusPriority(b.latest_report?.status);

      // Si même statut, trier par nom de station
      if (statusA === statusB) {
        return a.name.localeCompare(b.name);
      }

      return statusA - statusB;
    });


    // Pour la vue liste, appliquer la pagination côté frontend
    if (viewMode === 'list') {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginatedResults = sortedFiltered.slice(startIndex, startIndex + itemsPerPage);
      setFilteredStations(paginatedResults);
      setTotalItems(sortedFiltered.length);
    } else {
      // Pour la vue carte, montrer toutes les stations filtrées
      setFilteredStations(sortedFiltered);
      setTotalItems(sortedFiltered.length);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, allStationsForMap, filters, currentPage, itemsPerPage, showOnlyFavorites, favoritesVersion]);

  useEffect(() => {
    // Track page view on app load
    analytics.trackPageView('/', 'BamakoFuel - Accueil');
    amplitudeAnalytics.trackPage('Home');
    amplitudeAnalytics.trackAppOpened();

    loadStations();
  }, [loadStations]);

  // Effet pour appliquer les filtres
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Helper functions for cascading filters
  const getAvailableBrands = () => {
    let filteredStations = allStationsForMap;

    // Filter by selected commune
    if (filters.commune !== 'all') {
      filteredStations = filteredStations.filter(s =>
        s.commune.toLowerCase() === filters.commune.toLowerCase()
      );
    }

    // Filter by selected quartier
    if (filters.quartier !== 'all') {
      filteredStations = filteredStations.filter(s =>
        s.quartier?.toLowerCase() === filters.quartier.toLowerCase()
      );
    }

    return [...new Set(filteredStations.map(s => s.brand))].sort();
  };

  const getAvailableCommunes = () => {
    let filteredStations = allStationsForMap;

    // Filter by selected brand
    if (filters.brand !== 'all') {
      filteredStations = filteredStations.filter(s =>
        s.brand.toLowerCase() === filters.brand.toLowerCase()
      );
    }

    // Filter by selected quartier
    if (filters.quartier !== 'all') {
      filteredStations = filteredStations.filter(s =>
        s.quartier?.toLowerCase() === filters.quartier.toLowerCase()
      );
    }

    return [...new Set(filteredStations.map(s => s.commune))].sort();
  };

  const getAvailableQuartiers = () => {
    let filteredStations = allStationsForMap;

    // Filter by selected brand
    if (filters.brand !== 'all') {
      filteredStations = filteredStations.filter(s =>
        s.brand.toLowerCase() === filters.brand.toLowerCase()
      );
    }

    // Filter by selected commune
    if (filters.commune !== 'all') {
      filteredStations = filteredStations.filter(s =>
        s.commune.toLowerCase() === filters.commune.toLowerCase()
      );
    }

    return [...new Set(filteredStations.map(s => s.quartier).filter(Boolean))].sort();
  };

  const handleFilterChange = (key: string, value: string) => {
    // Clear selected station when using filters to prevent modal from reopening
    setSelectedStation(null);

    // Track filter usage
    analytics.trackEvent('filter_change', {
      filter_type: key,
      filter_value: value,
      total_results: filteredStations.length
    });
    amplitudeAnalytics.trackFilterUsed(key, value);

    // Hide filter hint when user interacts with filters
    if (showFilterHint) {
      setShowFilterHint(false);
    }

    // Create new filters object with cascading logic
    let newFilters = { ...filters, [key]: value };

    // Clear dependent filters when parent filter changes
    if (key === 'brand') {
      // When brand changes, clear commune and quartier
      newFilters.commune = 'all';
      newFilters.quartier = 'all';
    } else if (key === 'commune') {
      // When commune changes, clear quartier
      newFilters.quartier = 'all';
    }

    setFilters(newFilters);

    // Reset to first page when filters change
    setCurrentPage(1);
  };

  const clearFilters = () => {
    // Clear selected station when clearing filters
    setSelectedStation(null);

    // Track filter clear action
    analytics.trackEvent('filters_cleared', {
      previous_results: filteredStations.length,
      total_stations: stations.length
    });
    amplitudeAnalytics.track('Filters Cleared', {
      previous_results: filteredStations.length,
      total_stations: stations.length
    });

    setFilters({
      searchTerm: '',
      fuelType: 'all',
      status: 'all',
      brand: 'all',
      quartier: 'all',
      commune: 'all'
    });
    setCurrentPage(1); // Retourner à la première page

    // Les filtres seront appliqués automatiquement par applyFilters
  };

  // Gérer le changement de page
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Les données seront re-paginées automatiquement par applyFilters
  };

  // Gérer le changement de mode de vue
  const handleViewModeChange = (mode: 'list' | 'map' | 'activity') => {
    // Clear selected station when changing views (except when going to map from activity)
    if (mode !== 'map') {
      setSelectedStation(null);
    }

    setViewMode(mode);
    if (mode === 'map' && allStationsForMap.length === 0) {
      // Charger toutes les stations pour la carte si pas déjà fait
      loadAllStationsForMap();
    } else if (mode === 'list') {
      // Charger la première page pour la liste
      setCurrentPage(1);
      loadStationsPaginated(1, itemsPerPage);
    } else if (mode === 'activity') {
      // Le composant ActivityFeed gère ses propres données
    }
  };

  const goToHome = () => {
    clearFilters();
    setViewMode('map');
    setSelectedStation(null);
    setShowQuickReportModal(false);
    setShowSupportModal(false);
    setShowStationSuggestionModal(false);
    setShowLocationUpdateModal(false);
    setLocationUpdateStation(null);
    setShowContactModal(false);
    setFiltersExpanded(false);
  };

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback((station: Station) => {
    if (isFavorite(station.id)) {
      removeFromFavorites(station.id);
      analytics.trackEvent('favorite_removed', { station_id: station.id, station_name: station.name });
      amplitudeAnalytics.trackStationFavorited({
        id: station.id.toString(),
        name: station.name,
        brand: station.brand
      }, false);
    } else {
      addToFavorites({
        id: station.id,
        name: station.name,
        brand: station.brand,
        address: station.address,
        commune: station.commune
      });
      analytics.trackEvent('favorite_added', { station_id: station.id, station_name: station.name });
      amplitudeAnalytics.trackStationFavorited({
        id: station.id.toString(),
        name: station.name,
        brand: station.brand
      }, true);
    }
    setFavoritesVersion(prev => prev + 1); // Trigger re-render
  }, []);

  // Handle location update modal
  const handleLocationUpdate = useCallback((station: Station) => {
    setLocationUpdateStation(station);
    setShowLocationUpdateModal(true);
    analytics.trackEvent('location_update_opened', { station_id: station.id, station_name: station.name });
    amplitudeAnalytics.trackCriticalAction('Location Update Opened', {
      station_id: station.id.toString(),
      station_name: station.name
    });
  }, []);

  // Handle favorites filter toggle
  const toggleFavoritesFilter = () => {
    const favoriteIds = getFavoriteIds();

    // If user has no favorites and is trying to enable favorites filter
    if (!showOnlyFavorites && favoriteIds.length === 0) {
      alert('Vous n\'avez pas encore de stations favorites. Ajoutez vos stations préférées pour les retrouver facilement !');
      return;
    }

    setShowOnlyFavorites(!showOnlyFavorites);
    analytics.trackEvent('favorites_filter_toggled', {
      enabled: !showOnlyFavorites,
      favorites_count: favoriteIds.length
    });
    amplitudeAnalytics.track('Favorites Filter Toggled', {
      enabled: !showOnlyFavorites,
      favorites_count: favoriteIds.length
    });
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'available': return 'bg-green-500 text-white';
      case 'low': return 'bg-red-500 text-white'; // Même couleur que 'out'
      case 'out': return 'bg-red-500 text-white';
      default: return 'bg-red-500 text-white'; // "Pas d'info" même couleur que "Rupture"
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'available': return 'Disponible';
      case 'low': return 'Rupture';
      case 'out': return 'Rupture';
      default: return 'Rupture'; // "Pas d'info" devient "Rupture"
    }
  };


  const handleStationClick = useCallback((station: Station) => {
    // Track station interactions
    analytics.trackStationEvent('select', station.name, station.brand);
    amplitudeAnalytics.trackStationViewed({
      id: station.id.toString(),
      name: station.name,
      brand: station.brand,
      fuel_types: station.latest_report?.fuel_type ? [station.latest_report.fuel_type] : []
    });

    setSelectedStation(station);
    setShowQuickReportModal(true);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleReportSuccess = () => {
    // Track successful fuel report
    analytics.trackConversion('fuel_report_submitted', 1);

    loadStations(); // Recharger les données
    setShowQuickReportModal(false);
    setSelectedStation(null);
  };

  // Ne pas afficher le loading screen si on a déjà commencé à charger l'interface
  if (loading && !initialLoadComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initialisation de BamakoFuel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur de connexion</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadStations}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Android App Download Banner */}
      <AndroidDownloadBanner />

      {/* Mobile-First Header - Compact and Always Visible */}
      <header className="bg-gradient-to-r from-sky-400 to-blue-500 shadow-lg sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            {/* Compact Logo */}
            <button
              onClick={goToHome}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <img
                src="/icon.png"
                alt="BamakoFuel"
                className="h-8 w-auto object-contain"
              />
              <div>
                <h1 className="text-base font-black text-white">bkofuel</h1>
              </div>
            </button>

            {/* Mobile Action Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  analytics.trackEvent('modal_opened', { modal_type: 'station_suggestion', location: 'header' });
                  amplitudeAnalytics.trackModalOpened('Station Suggestion');
                  setShowStationSuggestionModal(true);
                }}
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                title="Ajouter une station"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>

              <button
                onClick={() => {
                  analytics.trackEvent('modal_opened', { modal_type: 'contact', location: 'header' });
                  amplitudeAnalytics.trackModalOpened('Contact');
                  setShowContactModal(true);
                }}
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                title="Contact"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </button>

              <button
                onClick={toggleFavoritesFilter}
                className={`p-2 rounded-full transition-colors ${
                  showOnlyFavorites
                    ? 'bg-red-500 text-white'
                    : 'text-white hover:bg-white hover:bg-opacity-20'
                }`}
                title={showOnlyFavorites ? 'Afficher toutes les stations' : 'Afficher uniquement les favoris'}
              >
                <svg className="w-5 h-5" fill={showOnlyFavorites ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>

              <button
                onClick={() => {
                  analytics.trackEvent('modal_opened', { modal_type: 'quick_report', location: 'header' });
                  amplitudeAnalytics.trackModalOpened('Quick Report');
                  setShowQuickReportModal(true);
                }}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white font-medium px-3 py-2 rounded-full shadow-lg flex items-center space-x-1 text-xs active:scale-95 transition-transform"
              >
                <span>⚡</span>
                <span className="hidden xs:inline">Signaler</span>
              </button>

              <button
                onClick={() => {
                  analytics.trackEvent('modal_opened', { modal_type: 'support', location: 'header' });
                  amplitudeAnalytics.trackModalOpened('Support');
                  setShowSupportModal(true);
                }}
                className="bg-gradient-to-r from-blue-500 to-green-500 text-white font-medium px-3 py-2 rounded-full shadow-lg flex items-center space-x-1 text-xs active:scale-95 transition-transform"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM7 8a5 5 0 0110 0v6h2l-2 2H7l-2-2h2V8z" />
                </svg>
                <span className="hidden xs:inline">Alertes</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile-First Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">

        {/* Horizontal Filter Chips - Mobile Style */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex flex-wrap gap-2 justify-start">
            {/* Status Filter Chips */}
            <button
              onClick={() => handleFilterChange('status', filters.status === 'available' ? 'all' : 'available')}
              className={`flex items-center space-x-1 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all active:scale-95 flex-shrink-0 ${
                filters.status === 'available'
                  ? 'bg-green-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>✅</span>
              <span>Disponible</span>
            </button>

            <button
              onClick={() => handleFilterChange('fuelType', filters.fuelType === 'gasoil' ? 'all' : 'gasoil')}
              className={`flex items-center space-x-1 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all active:scale-95 flex-shrink-0 ${
                filters.fuelType === 'gasoil'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>⛽</span>
              <span>Gasoil</span>
            </button>

            {/* Fuel Type Chips */}
            <button
              onClick={() => handleFilterChange('fuelType', filters.fuelType === 'essence' ? 'all' : 'essence')}
              className={`flex items-center space-x-1 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all active:scale-95 flex-shrink-0 ${
                filters.fuelType === 'essence'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>🛢️</span>
              <span>Essence</span>
            </button>


            {/* Search/More Filters Button */}
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="flex items-center space-x-1 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all active:scale-95 flex-shrink-0"
            >
              <span>🔍</span>
              <span className="hidden sm:inline">Rechercher</span>
            </button>

            {/* Clear Filters */}
            {(filters.searchTerm || filters.status !== 'all' || filters.fuelType !== 'all' || filters.brand !== 'all' || filters.commune !== 'all' || filters.quartier !== 'all') && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-1 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap bg-red-100 text-red-700 hover:bg-red-200 transition-all active:scale-95 flex-shrink-0"
              >
                <span>🔄</span>
                <span>Effacer</span>
              </button>
            )}
          </div>
        </div>

        {/* Extended Filters Modal - Only Shown When Expanded */}
        {filtersExpanded && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center sm:justify-center">
            <div className="bg-white w-full max-h-[80vh] overflow-y-auto sm:max-w-2xl sm:rounded-lg sm:max-h-[90vh]">
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Filtres avancés</h3>
                  <button
                    onClick={() => setFiltersExpanded(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <span className="text-xl">×</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Search Bar */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🔍 Recherche
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={filters.searchTerm}
                        onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                        placeholder="Nom, adresse, quartier..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {filters.searchTerm && (
                        <button
                          onClick={() => handleFilterChange('searchTerm', '')}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 rounded-full bg-gray-400 hover:bg-gray-600 text-white text-xs transition-colors flex items-center justify-center"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Location Filters */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🏢 Commune
                    </label>
                    <select
                      value={filters.commune}
                      onChange={(e) => handleFilterChange('commune', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">Toutes les communes</option>
                      {getAvailableCommunes().map(commune => (
                        <option key={commune} value={commune}>{commune}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      🏠 Quartier
                    </label>
                    <select
                      value={filters.quartier}
                      onChange={(e) => handleFilterChange('quartier', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">Tous les quartiers</option>
                      {getAvailableQuartiers().map(quartier => (
                        <option key={quartier} value={quartier}>{quartier}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ⚡ Marque
                    </label>
                    <select
                      value={filters.brand}
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">Toutes les marques</option>
                      {getAvailableBrands().map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={clearFilters}
                      className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                    >
                      🔄 Effacer tout
                    </button>
                    <button
                      onClick={() => setFiltersExpanded(false)}
                      className="flex-1 bg-blue-500 text-white py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                    >
                      Appliquer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile-First Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* View Toggle - Mobile Optimized with Smaller Font */}
          <div className="bg-white px-4 py-2 border-b border-gray-200 flex justify-center">
            <div className="flex bg-gray-100 rounded-full p-1 w-full max-w-sm">
              <button
                onClick={() => {
                  analytics.trackEvent('view_mode_change', { mode: 'map', previous_mode: viewMode });
                  amplitudeAnalytics.track('View Mode Changed', { mode: 'map', previous_mode: viewMode });
                  handleViewModeChange('map');
                }}
                className={`flex-1 px-2 py-2 text-xs font-medium rounded-full transition-all ${
                  viewMode === 'map'
                    ? 'bg-white text-blue-600 shadow-md'
                    : 'text-gray-600'
                }`}
              >
                🗺️ Carte
              </button>
              <button
                onClick={() => {
                  analytics.trackEvent('view_mode_change', { mode: 'list', previous_mode: viewMode });
                  amplitudeAnalytics.track('View Mode Changed', { mode: 'list', previous_mode: viewMode });
                  handleViewModeChange('list');
                }}
                className={`flex-1 px-2 py-2 text-xs font-medium rounded-full transition-all ${
                  viewMode === 'list'
                    ? 'bg-white text-blue-600 shadow-md'
                    : 'text-gray-600'
                }`}
              >
                📋 Liste
              </button>
              <button
                onClick={() => {
                  analytics.trackEvent('view_mode_change', { mode: 'activity', previous_mode: viewMode });
                  amplitudeAnalytics.track('View Mode Changed', { mode: 'activity', previous_mode: viewMode });
                  handleViewModeChange('activity');
                }}
                className={`flex-1 px-2 py-2 text-xs font-medium rounded-full transition-all ${
                  viewMode === 'activity'
                    ? 'bg-white text-blue-600 shadow-md'
                    : 'text-gray-600'
                }`}
              >
                🔴 Temps réel
              </button>
            </div>
          </div>

          {/* Results Count - Compact */}
          <div className="bg-white px-4 py-1 border-b border-gray-200">
            <p className="text-xs text-gray-600 text-center">
              {viewMode === 'list' ? (
                totalItems > 0
                  ? `${totalItems} station${totalItems !== 1 ? 's' : ''} • Page ${currentPage}/${totalPages}`
                  : loadingPage
                  ? 'Chargement...'
                  : 'Aucune station'
              ) : viewMode === 'activity' ? (
                'Activités récentes des stations'
              ) : (
                filteredStations.length > 0
                  ? `${filteredStations.length} station${filteredStations.length !== 1 ? 's' : ''}`
                  : allStationsForMap.length === 0
                  ? 'Chargement...'
                  : 'Aucune station'
              )}
            </p>
          </div>

          {/* Main Content Area - Fullscreen */}
          <div className="flex-1 relative overflow-hidden">
            {/* Fullscreen Map View */}
            {viewMode === 'map' && (
              <div className="absolute inset-0">
                {allStationsForMap.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <SkeletonLoader viewMode="map" />
                  </div>
                ) : (
                  <MapComponent
                    stations={filteredStations}
                    onStationClick={handleStationClick}
                    onFavoriteToggle={handleFavoriteToggle}
                    isFavorite={isFavorite}
                    onLocationUpdate={handleLocationUpdate}
                    selectedStation={selectedStation}
                  />
                )}
              </div>
            )}

            {/* Mobile-Optimized List View */}
            {viewMode === 'list' && (
              <div className="absolute inset-0 overflow-y-auto">
                <div className="p-4 space-y-3">
                  {loadingPage ? (
                    <SkeletonLoader viewMode="list" count={6} />
                  ) : filteredStations.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-gray-400 text-4xl mb-4">🔍</div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune station trouvée</h3>
                      <p className="text-gray-600 mb-4">Modifiez vos filtres pour voir plus de résultats.</p>
                      <button
                        onClick={clearFilters}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-full transition-colors active:scale-95"
                      >
                        Effacer les filtres
                      </button>
                    </div>
                  ) : (
                    filteredStations.map((station) => (
                      <div key={station.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 active:scale-[0.98] transition-transform">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 text-base">{station.name}</h3>
                            <p className="text-sm text-gray-600">{station.brand}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFavoriteToggle(station);
                              }}
                              className={`p-2 rounded-full transition-colors ${
                                isFavorite(station.id)
                                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                              title={isFavorite(station.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                            >
                              {isFavorite(station.id) ? '❤️' : '🤍'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLocationUpdate(station);
                              }}
                              className="p-2 rounded-full bg-gray-100 text-gray-400 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                              title="Corriger la position"
                            >
                              📍
                            </button>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(station.latest_report?.status)}`}>
                              {getStatusText(station.latest_report?.status)}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-1 text-sm text-gray-600 mb-3">
                          <p className="flex items-start">
                            <span className="mr-2">📍</span>
                            <span className="flex-1">{station.address}</span>
                          </p>
                          {station.quartier && (
                            <p className="flex items-center">
                              <span className="mr-2">🏠</span>
                              <span>{station.quartier}</span>
                            </p>
                          )}
                          <p className="flex items-center">
                            <span className="mr-2">🏢</span>
                            <span>{station.commune}</span>
                          </p>
                        </div>

                        {station.latest_report && (
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium">
                                🛢️ {station.latest_report.fuel_type === 'essence' ? 'Essence' :
                                     station.latest_report.fuel_type === 'diesel' ? 'Diesel' : 'Les deux'}
                              </span>
                              {station.latest_report.price && (
                                <span className="text-sm font-semibold text-green-600">
                                  {station.latest_report.price} FCFA/L
                                </span>
                              )}
                            </div>

                            {station.latest_report.comment && (
                              <p className="text-xs text-gray-600 mb-2">
                                💬 {station.latest_report.comment}
                              </p>
                            )}

                            {/* Votes/Contestations display */}
                            {((station.latest_report.confirm_count || 0) > 0 || (station.latest_report.contest_count || 0) > 0) && (
                              <div className="text-xs mb-2 space-y-1">
                                {(station.latest_report.confirm_count || 0) > 0 && (
                                  <p className="text-green-600">
                                    ✅ {station.latest_report.confirm_count} confirmations
                                  </p>
                                )}
                                {(station.latest_report.contest_count || 0) > 0 && (
                                  <p className="text-red-600 font-medium">
                                    ❌ {station.latest_report.contest_count} contestations
                                  </p>
                                )}
                              </div>
                            )}

                            <p className="text-xs text-gray-500">
                              🕒 {getRelativeTime(station.latest_report.created_at)}
                            </p>
                          </div>
                        )}

                        <button
                          onClick={() => handleStationClick(station)}
                          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-3 px-4 rounded-xl transition-all active:scale-95 shadow-md"
                        >
                          📝 Signaler une mise à jour
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination for List View */}
                {!loadingPage && totalPages > 1 && (
                  <div className="p-4 bg-white border-t border-gray-200">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      loading={loadingPage}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Activity Feed View */}
            {viewMode === 'activity' && (
              <div className="absolute inset-0 overflow-y-auto">
                <ActivityFeed
                  onStationClick={(stationId) => {
                    // Find the station and focus on it in map view
                    const station = allStationsForMap.find(s => s.id === stationId);
                    if (station) {
                      setSelectedStation(station);
                      setViewMode('map');
                      // Switch to map view to show the selected station
                    }
                  }}
                />
              </div>
            )}

          </div>
        </div>

      </main>


      {/* Modal de soutien */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />

      {/* Modal de signalement rapide */}
      <QuickReportModal
        stations={allStationsForMap}
        selectedStation={selectedStation}
        isOpen={showQuickReportModal}
        onClose={() => {
          setShowQuickReportModal(false);
          setSelectedStation(null);
        }}
        onSuccess={() => {
          loadStations(); // Recharger les données
          setShowQuickReportModal(false);
          setSelectedStation(null);
        }}
      />

      {/* Modal de suggestion de station */}
      <StationSuggestionModal
        isOpen={showStationSuggestionModal}
        onClose={() => setShowStationSuggestionModal(false)}
      />

      {/* Modal de correction de position */}
      <StationLocationUpdateModal
        isOpen={showLocationUpdateModal}
        onClose={() => {
          setShowLocationUpdateModal(false);
          setLocationUpdateStation(null);
        }}
        preSelectedStation={locationUpdateStation}
      />

      {/* Modal de contact */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      {/* Mobile Footer */}
      <footer className="bg-white border-t">
        <div className="px-4 py-3">
          <div className="text-center text-xs text-gray-500 space-y-2">
            <div>
              🚗 <a href="https://bkofuel.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold">bkofuel</a>
              <div className="mt-1">Plateforme communautaire</div>
            </div>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => {
                  analytics.trackEvent('modal_opened', { modal_type: 'support', location: 'footer' });
                  amplitudeAnalytics.trackModalOpened('Support');
                  setShowSupportModal(true);
                }}
                className="text-blue-500 underline"
              >
                💡 Notifications
              </button>
              <button
                onClick={() => {
                  analytics.trackEvent('modal_opened', { modal_type: 'contact', location: 'footer' });
                  amplitudeAnalytics.trackModalOpened('Contact');
                  setShowContactModal(true);
                }}
                className="text-green-600 underline"
              >
                📧 Contact
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;